/**
 * Offline-first queue for phone answers.
 *
 * Bar wifi drops out. The phone therefore keeps every answer locally
 * (localStorage, with try/catch), sends pending answers in one batch, and
 * retries until the server confirms them. Each edit gets a larger sequence
 * number, so the server ignores a late retry of an older edit.
 *
 * The functions here are pure (state in, state out) so they are easy to
 * test. The component owns the timers and the network calls.
 */

export type AnswerStatus = 'pending' | 'saved' | 'locked' | 'invalid';

export interface QueuedAnswer {
  questionId: string;
  partA: string;
  partB: string | null;
  seq: number;
  status: AnswerStatus;
}

export type QueueState = Record<string, QueuedAnswer>;

export interface ServerAnswer {
  questionId: string;
  partA: string;
  partB: string | null;
  clientSeq: number;
}

export interface SendResult {
  questionId: string;
  status: 'saved' | 'locked' | 'invalid';
  seq: number;
}

// Seconds since 2024-01-01 UTC. Fits the server's 32-bit column until 2092.
const EPOCH_2024 = Date.UTC(2024, 0, 1);
export function clockSeq(nowMs: number = Date.now()): number {
  return Math.max(1, Math.floor((nowMs - EPOCH_2024) / 1000));
}

/** Record an edit. The new sequence number is always larger than the old one. */
export function setDraft(
  state: QueueState,
  questionId: string,
  partA: string,
  partB: string | null,
  nowMs: number = Date.now(),
): QueueState {
  const prev = state[questionId];
  const seq = Math.max((prev?.seq ?? 0) + 1, clockSeq(nowMs));
  return { ...state, [questionId]: { questionId, partA, partB, seq, status: 'pending' } };
}

/** Pending answers to send, oldest edit first, at most `max`. */
export function pendingBatch(state: QueueState, max = 40) {
  return Object.values(state)
    .filter((a) => a.status === 'pending')
    .sort((a, b) => a.seq - b.seq)
    .slice(0, max)
    .map(({ questionId, partA, partB, seq }) => ({ questionId, partA, partB, seq }));
}

export function pendingCount(state: QueueState): number {
  return Object.values(state).filter((a) => a.status === 'pending').length;
}

/**
 * Apply the server's per-answer results. An answer edited again after the
 * batch left (a larger local seq) stays pending.
 */
export function applyResults(state: QueueState, results: SendResult[]): QueueState {
  const next = { ...state };
  for (const r of results) {
    const cur = next[r.questionId];
    if (!cur) continue;
    if (r.status === 'saved') {
      if (cur.seq <= r.seq) next[r.questionId] = { ...cur, status: 'saved' };
    } else {
      // Closed round or unknown question: no edit of it can be sent.
      next[r.questionId] = { ...cur, status: r.status };
    }
  }
  return next;
}

/**
 * Merge the server's copy of this team's answers into the local queue.
 * A local edit that the server has not seen yet (larger seq, still
 * pending) wins. Otherwise the server copy wins.
 */
export function mergeServer(state: QueueState, server: ServerAnswer[]): QueueState {
  const next = { ...state };
  for (const s of server) {
    const cur = next[s.questionId];
    if (cur && cur.status === 'pending' && cur.seq > s.clientSeq) continue;
    next[s.questionId] = {
      questionId: s.questionId,
      partA: s.partA,
      partB: s.partB,
      seq: s.clientSeq,
      status: 'saved',
    };
  }
  return next;
}

/** Mark pending answers of closed rounds as locked (they cannot be sent). */
export function lockRounds(state: QueueState, questionIdsInClosedRounds: Set<string>): QueueState {
  let changed = false;
  const next = { ...state };
  for (const [id, a] of Object.entries(state)) {
    if (a.status === 'pending' && questionIdsInClosedRounds.has(id)) {
      next[id] = { ...a, status: 'locked' };
      changed = true;
    }
  }
  return changed ? next : state;
}

/** Retry delay after `failures` failed sends: 1 s, 2 s, 4 s, ... up to 30 s. */
export function backoffMs(failures: number): number {
  return Math.min(30000, 1000 * 2 ** Math.max(0, failures - 1));
}

const storageKey = (pin: string, teamId: string) => `trivia:answers:${pin}:${teamId}`;

export function loadQueue(pin: string, teamId: string): QueueState {
  try {
    const raw = window.localStorage.getItem(storageKey(pin, teamId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as QueueState;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function saveQueue(pin: string, teamId: string, state: QueueState): void {
  try {
    window.localStorage.setItem(storageKey(pin, teamId), JSON.stringify(state));
  } catch {
    /* storage blocked or full: the in-memory copy still works for this tab */
  }
}
